import { FC, ReactNode, ChangeEventHandler, useContext, useEffect, useState } from 'react';
import ScreenContext from '@/contexts/screen';
import Select, { Props as SelectProps } from 'react-select';
import Button from '../Button';
import Drawer from '../Drawer';
import Checkbox from '../Checkbox';
import { ReactComponent as IFilter } from '@/assets/icons/filter.svg';
import { smallSizeWidth } from '@/constants';
import styles from './index.less';

export type OptionType = {
  label: ReactNode;
  value: string;
  disabled?: boolean;
}

export interface ResponsiveSelectProps {
  value: OptionType['value'];
  options: OptionType[];
  width: number;
  placeholder?: ReactNode;
  onChange: (value: OptionType['value']) => void;
  renderSmallItem?: (option: OptionType) => ReactNode;
}

const ResponsiveSelect: FC<ResponsiveSelectProps> = ({
  value, options, width, placeholder, onChange,
  renderSmallItem = ({ label }) => label
}) => {
  const { width: screenWidth } = useContext(ScreenContext);
  const [visible, setVisible] = useState<boolean>(false);
  const [checked, setChecked] = useState<ResponsiveSelectProps['value']>(value);

  useEffect(() => {
    setChecked(value);
  }, [value]);

  const handleChange: SelectProps['onChange'] = ({ value }) => {
    onChange(value);
  }

  const handleCheckedChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    if (target.checked) {
      setChecked(target.value);
    }
  }

  const handleCancelCheck = () => {
    setVisible(false);
    setChecked(value);
  }

  const handleSaveCheck = () => {
    setVisible(false);
    onChange(checked);
  }

  const renderSelect = () =>
    <Select
      value={options.filter(({ value: k }) => value === k)}
      options={options}
      onChange={handleChange}
      isClearable={false}
      isSearchable={false}
      placeholder={placeholder}
      theme={theme => ({
        ...theme,
        borderRadius: 4,
        colors: {
          ...theme.colors,
          primary25: '#F2F6FC',
          primary: '#409EFF',
          neutral80: '#606266'
        },
      })}
      styles={{
        control: provided => ({
          ...provided,
          minHeight: 34,
          height: 34,
          cursor: 'pointer',
          background: '#F2F3F7',
          borderColor: '#F2F3F7',
          borderRadius: 4
        }),
        indicatorsContainer: provided => ({ ...provided, width: 38, height: 32 }),
        indicatorSeparator: () => ({ display: 'none' }),
        valueContainer: provided => ({ ...provided, width, paddingLeft: 14 }),
        singleValue: provided => ({ ...provided }),
        option: provided => ({ ...provided, paddingLeft: 14 })
      }}
    />

  if (screenWidth < smallSizeWidth) {
    return (
      <>
        <Button type='text' onClick={() => setVisible(true)}>
          <IFilter />
        </Button>
        <Drawer title='筛选' visible={visible} onClose={handleCancelCheck}>
          <div className={styles.drawer}>
            <ul className={styles.list}>
              {options.map(({ label, value: k }) => (
                <li key={k} className={styles.item}>
                  <label className={styles.checkLabel}>
                    {renderSmallItem({ label, value: k })}
                    <Checkbox
                      value={k}
                      style={{ fontSize: 20 }}
                      checked={checked === k}
                      onChange={handleCheckedChange}
                    />
                  </label>
                </li>
              ))}
            </ul>
            <div className={styles.save}>
              <Button type='primary' className={styles.saveButton} onClick={handleSaveCheck}>保存</Button>
            </div>
          </div>
        </Drawer>
      </>
    )
  }
  
  return renderSelect();
}

export default ResponsiveSelect;
